/**
 * KYB submission error handling — stage-aware messages for traceability.
 * Matches backend KYB_ERROR_CODES and KYB_STAGES.
 */

export const KYB_STAGES = {
	SAVE: "save",
	SUBMIT: "submit",
	WALLET_PATCH: "wallet_patch",
} as const;

export type KybStage = (typeof KYB_STAGES)[keyof typeof KYB_STAGES];

/** Error augmented with stage for UI display */
export interface KybStagedError extends Error {
	stage?: KybStage;
}

/** User-facing messages per stage */
export const STAGE_ERROR_HINTS: Record<KybStage, string> = {
	[KYB_STAGES.SAVE]: "Failed to save your application. Please try again.",
	[KYB_STAGES.SUBMIT]: "Failed to submit your application. Please try again.",
	[KYB_STAGES.WALLET_PATCH]:
		"Application submitted, but we couldn't update the wallet status. It may already be correct.",
};

/** Check if error indicates "already submitted" (from tRPC or message) */
export function isAlreadySubmittedError(err: unknown): boolean {
	if (!err || typeof err !== "object") return false;
	const msg = (err as { message?: string }).message?.toLowerCase() ?? "";
	if (msg.includes("already submitted")) return true;
	// tRPC BAD_REQUEST with our message
	const data = (err as { data?: { code?: string } }).data;
	return data?.code === "BAD_REQUEST" && msg.includes("already submitted");
}

/** Normalize error to user-facing message with optional stage hint */
export function toUserMessage(err: unknown, stage?: KybStage): string {
	const base =
		err instanceof Error ? err.message : "Submission failed. Please try again.";
	if (stage && STAGE_ERROR_HINTS[stage]) {
		return `${STAGE_ERROR_HINTS[stage]} (${base})`;
	}
	return base;
}
